import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BoardFloorPlanData, FloorPlanItem, FloorPlanOpening, FloorPlanUnderlay, FloorPlanWall, FloorPlanWallEnd } from '../../../types';
import { catalogEntry, DEFAULT_OPENING_WIDTH_IN } from './floorPlanCatalog';
import { itemFromCatalog } from './floorPlanStairs';
import {
  addWallsToPlan,
  applyWallLength,
  applyWallSlide,
  applyWallVertex,
  finishWallEndDrag,
  hitTestWallEnd,
  JOIN_THRESHOLD_IN,
  nearestForeignEnd,
  wallEndPoint,
  withWallEdit,
} from './floorPlanConstraints';
import {
  applyDimDistance,
  dimDistance,
  dimPointKey,
  dimSelection,
  hitTestDimPoint,
  hitTestMeasure,
  listDimPoints,
  resolveDimPoint,
  sameDimPoint,
  type DimPoint,
} from './floorPlanDimensions';
import FloorPlanDrawing from './FloorPlanDrawing';
import {
  GRID_MINOR_IN,
  MIN_WALL_LENGTH_IN,
  applyFloorPlanWheel,
  clientPointToInches,
  clientPointToInchesFromView,
  floorPlanZoomLimits,
  zoomViewBox,
  clampOpeningOnWall,
  angleDegFromCenter,
  cropUnderlayEdge,
  fitPlanViewBox,
  genFloorPlanId,
  hasEndpointNear,
  formatInchesFtIn,
  hitTestDimension,
  hitTestItem,
  hitTestOpening,
  hitTestUnderlay,
  hitTestUnderlayCropEdge,
  hitsUnderlayRotateHandle,
  hitsUnderlayScaleHandle,
  nearestWall,
  parseDimensionToInches,
  normalizePlanExtents,
  scaleUnderlayFromPoints,
  scaleUnderlayFromVisibleWidth,
  setUnderlayRotation,
  orthogonalizeEnd,
  projectOnWall,
  snapPlanPoint,
  underlayAabb,
  underlayIsHidden,
  underlayIsLocked,
  underlayRotation,
  underlayRenderStyles,
  underlayVisibleAabb,
  wallDimensionAnchor,
  wallHandleHitInches,
  wallLength,
  wallsFromRect,
  type FloorPlanCropEdge,
  type FloorPlanViewBox,
} from './floorPlanMath';
import type { FloorPlanScaleRef, FloorPlanSelection, FloorPlanThickness, FloorPlanTool } from './floorPlanTypes';
import FloorPlanPresenceLayer from './FloorPlanPresenceLayer';
import { publishBoardCursor } from '../../../utils/boardPresenceService';

export interface FloorPlanPresenceAuth {
  dealId: string;
  uid: string;
  name: string;
  color: string;
  nodeId: string;
}

interface FloorPlanCanvasProps {
  data: BoardFloorPlanData;
  tool: FloorPlanTool;
  thickness: FloorPlanThickness;
  placeCatalogId: string | null;
  selected: FloorPlanSelection | null;
  onSelect: (next: FloorPlanSelection | null) => void;
  onChange: (next: BoardFloorPlanData) => void;
  onBeginEdit?: () => void;
  onToolChange?: (next: FloorPlanTool) => void;
  scaleRef?: FloorPlanScaleRef | null;
  onScaleRefChange?: (next: FloorPlanScaleRef | null) => void;
  snapEnabled?: boolean;
  presence?: FloorPlanPresenceAuth | null;
}

type DragState =
  | { kind: 'pan'; startClientX: number; startClientY: number; start: FloorPlanViewBox }
  | { kind: 'wall'; x1: number; y1: number }
  | { kind: 'rect'; x1: number; y1: number }
  | { kind: 'item'; id: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'opening'; id: string; startX: number; startY: number; origOffset: number }
  | { kind: 'wall-press'; id: string; startX: number; startY: number; orig: FloorPlanWall }
  | { kind: 'wall-move'; id: string; startX: number; startY: number; orig: FloorPlanWall }
  | { kind: 'wall-end'; id: string; end: FloorPlanWallEnd }
  | { kind: 'underlay'; id: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'underlay-scale'; id: string; orig: FloorPlanUnderlay }
  | { kind: 'underlay-crop'; id: string; edge: FloorPlanCropEdge; orig: FloorPlanUnderlay }
  | { kind: 'underlay-rotate'; id: string; orig: FloorPlanUnderlay; cx: number; cy: number; startDeg: number; origRot: number };

const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  data,
  tool,
  thickness,
  placeCatalogId,
  selected,
  onSelect,
  onChange,
  onBeginEdit,
  onToolChange,
  scaleRef,
  onScaleRefChange,
  snapEnabled = true,
  presence,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [viewBox, setViewBox] = useState<FloorPlanViewBox>({ x: -36, y: -36, w: 504, h: 360 });
  const [stageSize, setStageSize] = useState({ w: 800, h: 600 });
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);
  const [previewWalls, setPreviewWalls] = useState<FloorPlanWall[]>([]);
  const [ghostItem, setGhostItem] = useState<FloorPlanItem | null>(null);
  const [chain, setChain] = useState<{ x: number; y: number } | null>(null);
  const [snapHint, setSnapHint] = useState<{ x: number; y: number } | null>(null);
  const [dimEdit, setDimEdit] = useState<{ wallId: string; value: string } | null>(null);
  const [measureAnchor, setMeasureAnchor] = useState<DimPoint | null>(null);
  const [measureDriven, setMeasureDriven] = useState<DimPoint | null>(null);
  const [measureEdit, setMeasureEdit] = useState<string | null>(null);
  const [hoverDim, setHoverDim] = useState<DimPoint | null>(null);
  const [cursorIn, setCursorIn] = useState<{ x: number; y: number } | null>(null);
  const [scaleRefDraft, setScaleRefDraft] = useState('');
  const scaleRefCancelRef = useRef(false);
  const measureCancelRef = useRef(false);
  const dragRef = useRef<DragState | null>(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const fittedRef = useRef<FloorPlanViewBox>(viewBox);
  const hasFittedRef = useRef(false);
  const stageSizeRef = useRef({ w: 0, h: 0 });
  const viewBoxRef = useRef(viewBox);
  viewBoxRef.current = viewBox;
  const gestureScaleRef = useRef(1);
  const lastPresenceSentRef = useRef(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const sync = (forceFit = false) => {
      const { width, height } = el.getBoundingClientRect();
      if (width < 8 || height < 8) return;
      setStageSize((prev) => (prev.w === width && prev.h === height ? prev : { w: width, h: height }));
      const fitted = fitPlanViewBox(dataRef.current, width, height);
      fittedRef.current = fitted;
      if (!hasFittedRef.current || forceFit) {
        hasFittedRef.current = true;
        stageSizeRef.current = { w: width, h: height };
        setViewBox(fitted);
        return;
      }
      const prevSize = stageSizeRef.current;
      if (Math.abs(width - prevSize.w) < 1 && Math.abs(height - prevSize.h) < 1) return;
      stageSizeRef.current = { w: width, h: height };
      const aspect = width / Math.max(height, 1);
      setViewBox((current) => {
        const cx = current.x + current.w / 2;
        const cy = current.y + current.h / 2;
        const nw = current.w;
        const nh = nw / aspect;
        return { x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh };
      });
    };
    sync(true);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => sync(false));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !hasFittedRef.current) return;
    const { width, height } = el.getBoundingClientRect();
    if (width < 8 || height < 8) return;
    fittedRef.current = fitPlanViewBox(data, width, height);
  }, [data]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const pointFromClient = (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const vb = viewBoxRef.current;
      if (!svg) return { x: vb.x + vb.w / 2, y: vb.y + vb.h / 2 };
      return (
        clientPointToInches(svg, clientX, clientY, vb) ||
        clientPointToInchesFromView(vb, svg.getBoundingClientRect(), clientX, clientY)
      );
    };
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const pt = pointFromClient(e.clientX, e.clientY);
      const macPinch = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
      setViewBox((vb) =>
        applyFloorPlanWheel(vb, fittedRef.current, {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          svgW: rect.width,
          svgH: rect.height,
          focusX: pt.x,
          focusY: pt.y,
          macPinch,
        }),
      );
    };
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      gestureScaleRef.current = 1;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      const scale = Number((e as unknown as { scale?: number }).scale);
      if (!Number.isFinite(scale) || scale <= 0) return;
      const factor = gestureScaleRef.current / scale;
      gestureScaleRef.current = scale;
      const ge = e as unknown as { clientX?: number; clientY?: number };
      const pt = pointFromClient(ge.clientX ?? 0, ge.clientY ?? 0);
      setViewBox((vb) => zoomViewBox(vb, factor, pt.x, pt.y, floorPlanZoomLimits(fittedRef.current)));
    };
    el.addEventListener('wheel', onWheelNative, { passive: false });
    el.addEventListener('gesturestart', onGestureStart, { capture: true });
    el.addEventListener('gesturechange', onGestureChange, { capture: true });
    el.addEventListener('gestureend', onGestureStart, { capture: true });
    return () => {
      el.removeEventListener('wheel', onWheelNative);
      el.removeEventListener('gesturestart', onGestureStart, true);
      el.removeEventListener('gesturechange', onGestureChange, true);
      el.removeEventListener('gestureend', onGestureStart, true);
    };
  }, []);

  useEffect(() => {
    if (tool !== 'wall') setChain(null);
    if (tool !== 'wall' && tool !== 'rect') setPreviewWalls([]);
    if (tool !== 'dimension') {
      setMeasureAnchor(null);
      setMeasureDriven(null);
      setMeasureEdit(null);
      setHoverDim(null);
      setCursorIn(null);
    }
  }, [tool]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(true);
      if (e.key === 'Alt') setAltHeld(true);
      if (e.key === 'Escape' && scaleRef) {
        e.preventDefault();
        e.stopPropagation();
        scaleRefCancelRef.current = true;
        onScaleRefChange?.(null);
        setScaleRefDraft('');
        return;
      }
      if (e.key === 'Escape' && tool === 'dimension') {
        e.preventDefault();
        e.stopPropagation();
        if (measureEdit != null) {
          measureCancelRef.current = true;
          setMeasureEdit(null);
          setMeasureDriven(null);
          return;
        }
        if (measureAnchor) {
          setMeasureAnchor(null);
          setHoverDim(null);
          return;
        }
        onToolChange?.('select');
        return;
      }
      if (e.key === 'Escape' && (chain || previewWalls.length || dimEdit || tool === 'wall' || tool === 'rect')) {
        e.preventDefault();
        e.stopPropagation();
        setChain(null);
        setPreviewWalls([]);
        setDimEdit(null);
        setSnapHint(null);
        dragRef.current = null;
        onToolChange?.('select');
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
      if (e.key === 'Alt') setAltHeld(false);
    };
    const onBlur = () => {
      setSpaceHeld(false);
      setAltHeld(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [chain, previewWalls.length, dimEdit, measureAnchor, measureEdit, tool, onToolChange, scaleRef, onScaleRefChange]);

  const toInches = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return null;
    return clientPointToInches(svg, clientX, clientY, viewBoxRef.current);
  }, []);

  const pickInches = () => Math.max(12, viewBox.w * 0.018);
  const pxPerIn = stageSize.w / Math.max(viewBox.w, 1);
  const snapOn = snapEnabled && !altHeld;
  const snapGrid = snapOn ? GRID_MINOR_IN : 0;
  const snapPt = (x: number, y: number, walls: FloorPlanWall[] = []) => snapPlanPoint(x, y, walls, snapOn);

  const enterSelect = (next: FloorPlanSelection) => {
    onToolChange?.('select');
    onSelect(next);
  };

  const startDimEdit = (wall: FloorPlanWall) => {
    enterSelect({ kind: 'wall', id: wall.id });
    setDimEdit({ wallId: wall.id, value: formatInchesFtIn(wallLength(wall)) });
    dragRef.current = null;
  };

  const applyScaleRef = (raw: string) => {
    if (!scaleRef?.a || !scaleRef.b) return;
    const inches = parseDimensionToInches(raw, 'ft');
    if (!inches) return;
    const current = (dataRef.current.underlays || []).find((entry) => entry.id === scaleRef.underlayId);
    if (!current || underlayIsLocked(current)) return;
    onBeginEdit?.();
    onChange(
      normalizePlanExtents({
        ...dataRef.current,
        underlays: (dataRef.current.underlays || []).map((entry) =>
          entry.id === current.id ? scaleUnderlayFromPoints(entry, scaleRef.a!, scaleRef.b!, inches) : entry,
        ),
      }),
    );
    onScaleRefChange?.(null);
    setScaleRefDraft('');
  };

  const commitDimEdit = (raw: string) => {
    if (!dimEdit) return;
    const inches = parseDimensionToInches(raw, 'ft');
    setDimEdit(null);
    if (!inches) return;
    onBeginEdit?.();
    onChange(
      normalizePlanExtents(
        withWallEdit(dataRef.current, applyWallLength(dataRef.current.walls, dataRef.current.constraints, dimEdit.wallId, inches, 'start', snapGrid)),
      ),
    );
  };

  const dimHitIn = () => Math.max(8, viewBox.w * 0.012);

  const pickDimPoint = (x: number, y: number): DimPoint => {
    const hit = hitTestDimPoint(dataRef.current, x, y, dimHitIn());
    if (hit) return hit;
    const snapped = snapPt(x, y, dataRef.current.walls);
    return { kind: 'free', x: snapped.x, y: snapped.y };
  };

  const dropMeasure = (anchor: DimPoint, driven: DimPoint) => {
    const a = resolveDimPoint(dataRef.current, anchor);
    const b = resolveDimPoint(dataRef.current, driven);
    if (!a || !b || Math.hypot(b.x - a.x, b.y - a.y) < 2) return null;
    const measure = {
      id: genFloorPlanId('measure'),
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y,
    };
    onBeginEdit?.();
    onChange({
      ...dataRef.current,
      measures: [...(dataRef.current.measures || []), measure],
    });
    return measure;
  };

  const commitMeasureEdit = (raw: string) => {
    if (!measureAnchor || !measureDriven) {
      setMeasureEdit(null);
      return;
    }
    const inches = parseDimensionToInches(raw, 'ft');
    setMeasureEdit(null);
    if (!inches) {
      setMeasureDriven(null);
      return;
    }
    const next = applyDimDistance(dataRef.current, measureAnchor, measureDriven, inches, snapGrid);
    if (next) {
      onBeginEdit?.();
      onChange(next);
      const sel = dimSelection(measureDriven);
      if (sel) onSelect(sel);
    }
    setMeasureAnchor(null);
    setMeasureDriven(null);
    setHoverDim(null);
  };

  const placeOpening = (kind: FloorPlanOpening['kind'], x: number, y: number) => {
    const wall = nearestWall(data.walls, x, y, 18);
    if (!wall) return;
    const hit = projectOnWall(wall, x, y);
    const widthIn = DEFAULT_OPENING_WIDTH_IN[kind];
    const sized = clampOpeningOnWall(wall, { offsetIn: hit.offsetIn - widthIn / 2, widthIn });
    const opening: FloorPlanOpening = {
      id: genFloorPlanId(kind),
      kind,
      wallId: wall.id,
      offsetIn: sized.offsetIn,
      widthIn: sized.widthIn,
      swing: kind === 'door' ? 'left' : undefined,
    };
    onBeginEdit?.();
    onChange({ ...data, openings: [...data.openings, opening] });
    onSelect({ kind: 'opening', id: opening.id });
  };

  const placeItemAt = (catalogId: string, x: number, y: number) => {
    const entry = catalogEntry(catalogId);
    if (!entry) return;
    const snapped = snapPt(x - entry.widthIn / 2, y - entry.depthIn / 2, data.walls);
    const item = itemFromCatalog(catalogId, snapped.x, snapped.y, genFloorPlanId(catalogId));
    if (!item) return;
    onBeginEdit?.();
    onChange(normalizePlanExtents({ ...data, items: [...data.items, item] }));
    onSelect({ kind: 'item', id: item.id });
  };

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button === 1 || spaceHeld || e.button === 2) {
      e.preventDefault();
      dragRef.current = {
        kind: 'pan',
        startClientX: e.clientX,
        startClientY: e.clientY,
        start: viewBox,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const pt = toInches(e.clientX, e.clientY);
    if (!pt) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pick = pickInches();

    if (scaleRef) {
      const scaleTarget = (data.underlays || []).find((entry) => entry.id === scaleRef.underlayId);
      if (underlayIsLocked(scaleTarget)) return;
      if (!scaleRef.a) {
        onScaleRefChange?.({ ...scaleRef, a: { x: pt.x, y: pt.y } });
      } else if (!scaleRef.b) {
        if (Math.hypot(pt.x - scaleRef.a.x, pt.y - scaleRef.a.y) >= 6) {
          onScaleRefChange?.({ ...scaleRef, b: { x: pt.x, y: pt.y } });
          setScaleRefDraft('');
        }
      }
      return;
    }

    if (tool === 'dimension') {
      const hit = pickDimPoint(pt.x, pt.y);
      if (!measureAnchor) {
        setMeasureAnchor(hit);
        setMeasureDriven(null);
        setMeasureEdit(null);
        return;
      }
      if (sameDimPoint(measureAnchor, hit)) return;
      if (measureAnchor.kind === 'free' || hit.kind === 'free') {
        const placed = dropMeasure(measureAnchor, hit);
        if (placed) onSelect({ kind: 'measure', id: placed.id });
        setMeasureAnchor(null);
        setMeasureDriven(null);
        setMeasureEdit(null);
        setHoverDim(null);
        return;
      }
      setMeasureDriven(hit);
      const dist = dimDistance(data, measureAnchor, hit);
      setMeasureEdit(dist ? formatInchesFtIn(dist) : '');
      return;
    }

    if (tool === 'select') {
      const dimWall = hitTestDimension(data.walls, pt.x, pt.y, viewBox, pxPerIn);
      if (dimWall) {
        startDimEdit(dimWall);
        return;
      }
    }

    if ((tool === 'select' || (tool === 'wall' && !chain)) && data.walls.length) {
      const hitEnd = hitTestWallEnd(data.walls, pt.x, pt.y, wallHandleHitInches(viewBox));
      if (hitEnd && (tool === 'select' || selected?.id === hitEnd.wall.id)) {
        enterSelect({ kind: 'wall', id: hitEnd.wall.id });
        onBeginEdit?.();
        dragRef.current = { kind: 'wall-end', id: hitEnd.wall.id, end: hitEnd.end };
        return;
      }
      if (tool === 'wall' && !chain && !hitEnd) {
        const wall = nearestWall(data.walls, pt.x, pt.y, pick);
        if (wall) {
          enterSelect({ kind: 'wall', id: wall.id });
          dragRef.current = {
            kind: 'wall-press',
            id: wall.id,
            startX: pt.x,
            startY: pt.y,
            orig: { ...wall },
          };
          return;
        }
      }
    }

    if (tool === 'wall') {
      const snapped = snapPt(pt.x, pt.y, data.walls);
      const start = chain || snapped;
      const ortho = chain ? orthogonalizeEnd(start.x, start.y, pt.x, pt.y) : { x2: start.x, y2: start.y };
      const end = chain ? snapPt(ortho.x2, ortho.y2, data.walls) : start;
      onBeginEdit?.();
      dragRef.current = { kind: 'wall', x1: start.x, y1: start.y };
      setPreviewWalls([
        {
          id: 'preview',
          x1: start.x,
          y1: start.y,
          x2: end.x,
          y2: end.y,
          thicknessIn: thickness,
        },
      ]);
      return;
    }

    if (tool === 'rect') {
      const start = snapPt(pt.x, pt.y, data.walls);
      onBeginEdit?.();
      dragRef.current = { kind: 'rect', x1: start.x, y1: start.y };
      setPreviewWalls([]);
      return;
    }

    if (tool === 'door' || tool === 'window' || tool === 'garage_door') {
      placeOpening(tool, pt.x, pt.y);
      return;
    }

    if (tool === 'place' && placeCatalogId) {
      placeItemAt(placeCatalogId, pt.x, pt.y);
      return;
    }

    if (tool === 'select' && selected?.kind === 'underlay') {
      const current = (data.underlays || []).find((underlay) => underlay.id === selected.id);
      if (current && !underlayIsLocked(current) && !underlayIsHidden(current) && hitsUnderlayRotateHandle(current, pt.x, pt.y, viewBox)) {
        const box = underlayAabb(current);
        onBeginEdit?.();
        dragRef.current = {
          kind: 'underlay-rotate',
          id: current.id,
          orig: { ...current },
          cx: box.x + box.w / 2,
          cy: box.y + box.h / 2,
          startDeg: angleDegFromCenter(box.x + box.w / 2, box.y + box.h / 2, pt.x, pt.y),
          origRot: underlayRotation(current),
        };
        return;
      }
      if (current && !underlayIsLocked(current) && hitsUnderlayScaleHandle(current, pt.x, pt.y, viewBox)) {
        onBeginEdit?.();
        dragRef.current = { kind: 'underlay-scale', id: current.id, orig: { ...current } };
        return;
      }
      const cropEdge =
        current && !underlayIsLocked(current) ? hitTestUnderlayCropEdge(current, pt.x, pt.y, viewBox) : null;
      if (current && cropEdge) {
        onBeginEdit?.();
        dragRef.current = { kind: 'underlay-crop', id: current.id, edge: cropEdge, orig: { ...current } };
        return;
      }
    }

    if (tool === 'select') {
      const hitEnd = hitTestWallEnd(data.walls, pt.x, pt.y, wallHandleHitInches(viewBox));
      if (hitEnd) {
        onSelect({ kind: 'wall', id: hitEnd.wall.id });
        onBeginEdit?.();
        dragRef.current = { kind: 'wall-end', id: hitEnd.wall.id, end: hitEnd.end };
        return;
      }
    }

    const measureHit = hitTestMeasure(data.measures, pt.x, pt.y, Math.max(6, viewBox.w * 0.01));
    if (measureHit) {
      onSelect({ kind: 'measure', id: measureHit.id });
      return;
    }
    const item = hitTestItem(data.items, pt.x, pt.y);
    if (item) {
      onSelect({ kind: 'item', id: item.id });
      onBeginEdit?.();
      dragRef.current = {
        kind: 'item',
        id: item.id,
        startX: pt.x,
        startY: pt.y,
        origX: item.x,
        origY: item.y,
      };
      return;
    }
    const opening = hitTestOpening(data.walls, data.openings, pt.x, pt.y);
    if (opening) {
      onSelect({ kind: 'opening', id: opening.id });
      onBeginEdit?.();
      dragRef.current = {
        kind: 'opening',
        id: opening.id,
        startX: pt.x,
        startY: pt.y,
        origOffset: opening.offsetIn,
      };
      return;
    }
    const wall = nearestWall(data.walls, pt.x, pt.y, pick);
    if (wall) {
      onSelect({ kind: 'wall', id: wall.id });
      dragRef.current = {
        kind: 'wall-press',
        id: wall.id,
        startX: pt.x,
        startY: pt.y,
        orig: { ...wall },
      };
      return;
    }
    const underlay = hitTestUnderlay(data.underlays, pt.x, pt.y);
    if (underlay) {
      onSelect({ kind: 'underlay', id: underlay.id });
      if (underlayIsLocked(underlay)) {
        dragRef.current = null;
        return;
      }
      onBeginEdit?.();
      dragRef.current = {
        kind: 'underlay',
        id: underlay.id,
        startX: pt.x,
        startY: pt.y,
        origX: underlay.x,
        origY: underlay.y,
      };
      return;
    }
    onSelect(null);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (presence) {
      const now = Date.now();
      if (now - lastPresenceSentRef.current >= 150) {
        const at = toInches(e.clientX, e.clientY);
        if (at) {
          lastPresenceSentRef.current = now;
          void publishBoardCursor(presence.dealId, {
            uid: presence.uid,
            name: presence.name,
            color: presence.color,
            x: at.x,
            y: at.y,
            surface: 'floorplan',
            nodeId: presence.nodeId,
          });
        }
      }
    }
    const drag = dragRef.current;
    const pt = toInches(e.clientX, e.clientY);

    if (!drag && tool === 'wall' && chain && pt) {
      const ortho = orthogonalizeEnd(chain.x, chain.y, pt.x, pt.y);
      const end = snapPt(ortho.x2, ortho.y2, data.walls);
      setPreviewWalls([
        {
          id: 'preview',
          x1: chain.x,
          y1: chain.y,
          x2: end.x,
          y2: end.y,
          thicknessIn: thickness,
        },
      ]);
    }

    if (!drag && tool === 'dimension' && pt) {
      setCursorIn(pt);
      setHoverDim(pickDimPoint(pt.x, pt.y));
    } else if (tool !== 'dimension') {
      setHoverDim(null);
      setCursorIn(null);
    }

    if (!drag && tool === 'place' && placeCatalogId && pt) {
      const entry = catalogEntry(placeCatalogId);
      if (entry) {
        const snapped = snapPt(pt.x - entry.widthIn / 2, pt.y - entry.depthIn / 2, data.walls);
        setGhostItem(itemFromCatalog(placeCatalogId, snapped.x, snapped.y, 'ghost'));
      }
    } else if (tool !== 'place') {
      setGhostItem(null);
    }

    if (!drag) return;

    if (drag.kind === 'pan') {
      const svg = svgRef.current;
      const rect = svg?.getBoundingClientRect();
      if (!rect || rect.width < 1) return;
      const dx = ((e.clientX - drag.startClientX) / rect.width) * drag.start.w;
      const dy = ((e.clientY - drag.startClientY) / rect.height) * drag.start.h;
      setViewBox({
        ...drag.start,
        x: drag.start.x - dx,
        y: drag.start.y - dy,
      });
      return;
    }

    if (!pt) return;

    if (drag.kind === 'wall') {
      const ortho = orthogonalizeEnd(drag.x1, drag.y1, pt.x, pt.y);
      const end = snapPt(ortho.x2, ortho.y2, data.walls);
      setPreviewWalls([
        {
          id: 'preview',
          x1: drag.x1,
          y1: drag.y1,
          x2: end.x,
          y2: end.y,
          thicknessIn: thickness,
        },
      ]);
      return;
    }

    if (drag.kind === 'rect') {
      const end = snapPt(pt.x, pt.y, data.walls);
      setPreviewWalls(
        wallsFromRect(drag.x1, drag.y1, end.x, end.y, thickness).map((wall, i) => ({
          ...wall,
          id: `preview-${i}`,
        })),
      );
      return;
    }

    if (drag.kind === 'item') {
      const dx = pt.x - drag.startX;
      const dy = pt.y - drag.startY;
      const snapped = snapPt(drag.origX + dx, drag.origY + dy);
      onChange({
        ...data,
        items: data.items.map((item) =>
          item.id === drag.id ? { ...item, x: snapped.x, y: snapped.y } : item,
        ),
      });
      return;
    }

    if (drag.kind === 'opening') {
      const opening = data.openings.find((o) => o.id === drag.id);
      const wall = opening && data.walls.find((w) => w.id === opening.wallId);
      if (!opening || !wall) return;
      const hit = projectOnWall(wall, pt.x, pt.y);
      const sized = clampOpeningOnWall(wall, { offsetIn: hit.offsetIn - opening.widthIn / 2, widthIn: opening.widthIn });
      onChange({
        ...data,
        openings: data.openings.map((o) => (o.id === drag.id ? { ...o, offsetIn: sized.offsetIn } : o)),
      });
      return;
    }

    if (drag.kind === 'wall-press') {
      if (Math.hypot(pt.x - drag.startX, pt.y - drag.startY) < 6) return;
      onBeginEdit?.();
      dragRef.current = { ...drag, kind: 'wall-move' };
    }

    if (dragRef.current?.kind === 'wall-move') {
      const move = dragRef.current;
      const dx = snapPt(pt.x, pt.y).x - snapPt(move.startX, move.startY).x;
      const dy = snapPt(pt.x, pt.y).y - snapPt(move.startX, move.startY).y;
      onChange(withWallEdit(data, applyWallSlide(data.walls, data.constraints, move.id, move.orig, dx, dy, snapGrid)));
      return;
    }

    if (drag.kind === 'wall-end') {
      const snapped = snapPt(pt.x, pt.y, data.walls.filter((wall) => wall.id !== drag.id));
      const magnet = nearestForeignEnd(data.walls, drag.id, snapped.x, snapped.y, Math.max(JOIN_THRESHOLD_IN, pickInches()));
      const magnetWall = magnet ? data.walls.find((wall) => wall.id === magnet.wallId) : undefined;
      const target = magnet && magnetWall ? wallEndPoint(magnetWall, magnet.end) : snapped;
      setSnapHint(magnet ? target : null);
      onChange(
        withWallEdit(
          data,
          applyWallVertex(data.walls, data.constraints, drag.id, drag.end, target.x, target.y, {
            snap: true,
            grid: snapGrid,
          }),
        ),
      );
      return;
    }

    if (drag.kind === 'underlay') {
      const live = (data.underlays || []).find((entry) => entry.id === drag.id);
      if (underlayIsLocked(live)) return;
      const snapped = snapPt(drag.origX + (pt.x - drag.startX), drag.origY + (pt.y - drag.startY));
      onChange({
        ...data,
        underlays: (data.underlays || []).map((underlay) =>
          underlay.id === drag.id ? { ...underlay, x: snapped.x, y: snapped.y } : underlay,
        ),
      });
      return;
    }

    if (drag.kind === 'underlay-scale') {
      if (underlayIsLocked(drag.orig)) return;
      const vis = underlayVisibleAabb(drag.orig);
      const aabbW = Math.max(24, snapPt(pt.x, pt.y).x - vis.x);
      onChange({
        ...data,
        underlays: (data.underlays || []).map((underlay) =>
          underlay.id === drag.id ? scaleUnderlayFromVisibleWidth(drag.orig, aabbW) : underlay,
        ),
      });
      return;
    }

    if (drag.kind === 'underlay-rotate') {
      if (underlayIsLocked(drag.orig)) return;
      const nextDeg = drag.origRot + (angleDegFromCenter(drag.cx, drag.cy, pt.x, pt.y) - drag.startDeg);
      onChange({
        ...data,
        underlays: (data.underlays || []).map((underlay) =>
          underlay.id === drag.id ? setUnderlayRotation(drag.orig, nextDeg, snapOn) : underlay,
        ),
      });
      return;
    }

    if (drag.kind === 'underlay-crop') {
      if (underlayIsLocked(drag.orig)) return;
      const world = drag.edge === 'left' || drag.edge === 'right' ? pt.x : pt.y;
      onChange({
        ...data,
        underlays: (data.underlays || []).map((underlay) =>
          underlay.id === drag.id ? cropUnderlayEdge(drag.orig, drag.edge, world) : underlay,
        ),
      });
    }
  };

  const onPointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    const current = dataRef.current;

    if (drag?.kind === 'wall' && previewWalls[0] && wallLength(previewWalls[0]) >= MIN_WALL_LENGTH_IN) {
      const preview = previewWalls[0];
      const wall: FloorPlanWall = {
        id: genFloorPlanId('wall'),
        x1: preview.x1,
        y1: preview.y1,
        x2: preview.x2,
        y2: preview.y2,
        thicknessIn: thickness,
      };
      onChange(addWallsToPlan(current, [wall]));
      onSelect({ kind: 'wall', id: wall.id });
      if (hasEndpointNear(current.walls, wall.x2, wall.y2, 8)) {
        setChain(null);
        setPreviewWalls([]);
        onToolChange?.('select');
      } else {
        setChain({ x: wall.x2, y: wall.y2 });
        setPreviewWalls([]);
      }
      return;
    }

    if (drag?.kind === 'wall') {
      setChain({ x: drag.x1, y: drag.y1 });
      setPreviewWalls([]);
      return;
    }

    if (drag?.kind === 'rect' && previewWalls.length === 4) {
      onChange(addWallsToPlan(current, previewWalls.map((wall) => ({
        ...wall,
        id: genFloorPlanId('wall'),
      }))));
      setPreviewWalls([]);
      setChain(null);
      onToolChange?.('select');
      return;
    }

    if (drag?.kind === 'wall-end') {
      onChange(finishWallEndDrag(current, drag.id, drag.end, Math.max(JOIN_THRESHOLD_IN, 12), snapGrid));
      setSnapHint(null);
      return;
    }

    if (
      drag?.kind === 'item' ||
      drag?.kind === 'wall-move' ||
      drag?.kind === 'underlay' ||
      drag?.kind === 'underlay-scale' ||
      drag?.kind === 'underlay-crop' ||
      drag?.kind === 'underlay-rotate'
    ) {
      onChange(normalizePlanExtents(current));
    }

    setPreviewWalls([]);
  };

  const cursor =
    spaceHeld || dragRef.current?.kind === 'pan'
      ? 'grab'
      : scaleRef || tool !== 'select'
        ? 'crosshair'
        : 'default';

  return (
    <div
      ref={wrapRef}
      className={`fp-canvas${spaceHeld ? ' is-panning' : ''}`}
    >
      {scaleRef && (
        <div className="fp-hint">
          {!scaleRef.a
            ? 'Click the first point on the trace (a known dimension).'
            : !scaleRef.b
              ? 'Click the second point.'
              : 'Type the real-world distance between those points.'}
        </div>
      )}
      {tool === 'dimension' && !scaleRef && (
        <div className="fp-hint">
          {!measureAnchor
            ? 'Click a wall, furniture, or anywhere on the model to start a measure.'
            : measureEdit == null
              ? 'Click the second point.'
              : 'Type the distance to move that point.'}
        </div>
      )}
      {data.walls.length === 0 && previewWalls.length === 0 && !(data.underlays || []).some((entry) => !underlayIsHidden(entry)) && !scaleRef && tool !== 'dimension' && (
        <div className="fp-hint">
          Click to start a wall, click again to place it. Keep clicking to continue a shape — L, T, or any outline. Escape starts a new run.
        </div>
      )}
      <div className="fp-underlays">
        {(data.underlays || []).filter((underlay) => !underlayIsHidden(underlay)).map((underlay) => {
          const styles = underlayRenderStyles(underlay, viewBox);
          return (
            <div key={underlay.id} className="fp-underlay-clip" style={styles.clip}>
              <img
                className="fp-underlay"
                src={underlay.url}
                alt={underlay.label || 'Trace image'}
                draggable={false}
                style={styles.image}
              />
            </div>
          );
        })}
      </div>
      <svg
        ref={svgRef}
        className="fp-svg"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        <FloorPlanDrawing
          data={data}
          selected={selected}
          previewWalls={previewWalls}
          ghostItem={ghostItem ?? undefined}
          showHandles={selected?.kind === 'wall' && tool !== 'dimension'}
          showDimensions={tool !== 'dimension'}
          hideDimensionWallId={dimEdit?.wallId}
          snapHint={snapHint}
          scaleRef={scaleRef}
          dimMarks={
            tool === 'dimension'
              ? (() => {
                  const marks = listDimPoints(data).reduce<
                    Array<{ key: string; x: number; y: number; active?: boolean; hover?: boolean }>
                  >((list, point) => {
                    const at = resolveDimPoint(data, point);
                    if (!at) return list;
                    list.push({
                      key: dimPointKey(point),
                      x: at.x,
                      y: at.y,
                      active:
                        !!(measureAnchor && sameDimPoint(measureAnchor, point)) ||
                        !!(measureDriven && sameDimPoint(measureDriven, point)),
                      hover: !!(hoverDim && hoverDim.kind !== 'free' && sameDimPoint(hoverDim, point)),
                    });
                    return list;
                  }, []);
                  const extras = [measureAnchor, measureDriven, hoverDim].filter(
                    (point): point is Extract<DimPoint, { kind: 'free' }> => !!point && point.kind === 'free',
                  );
                  extras.forEach((point) => {
                    marks.push({
                      key: dimPointKey(point),
                      x: point.x,
                      y: point.y,
                      active: !!(measureAnchor && sameDimPoint(measureAnchor, point)),
                      hover: !!(hoverDim && sameDimPoint(hoverDim, point)),
                    });
                  });
                  return marks;
                })()
              : undefined
          }
          measureLine={
            tool === 'dimension' && measureAnchor
              ? (() => {
                  const a = resolveDimPoint(data, measureAnchor);
                  const bPoint =
                    measureDriven ||
                    (hoverDim && !sameDimPoint(measureAnchor, hoverDim) ? hoverDim : null) ||
                    (cursorIn ? { kind: 'free' as const, x: cursorIn.x, y: cursorIn.y } : null);
                  const b = bPoint ? resolveDimPoint(data, bPoint) : null;
                  if (!a || !b) return null;
                  const dist = Math.hypot(b.x - a.x, b.y - a.y);
                  return {
                    a,
                    b,
                    label: dist >= 2 ? formatInchesFtIn(dist) : undefined,
                  };
                })()
              : null
          }
          viewBox={viewBox}
          pxPerIn={pxPerIn}
        />
      </svg>
      {presence && (
        <FloorPlanPresenceLayer
          dealId={presence.dealId}
          selfUid={presence.uid}
          nodeId={presence.nodeId}
          viewBox={viewBox}
        />
      )}
      {scaleRef?.a && scaleRef.b && (
        <input
          className="fp-dim-edit"
          value={scaleRefDraft}
          autoFocus
          placeholder="20'"
          aria-label="Real distance between points"
          style={{
            left: `${((((scaleRef.a.x + scaleRef.b.x) / 2) - viewBox.x) / viewBox.w) * 100}%`,
            top: `${((((scaleRef.a.y + scaleRef.b.y) / 2) - viewBox.y) / viewBox.h) * 100}%`,
          }}
          onChange={(e) => setScaleRefDraft(e.target.value)}
          onBlur={() => {
            if (scaleRefCancelRef.current) {
              scaleRefCancelRef.current = false;
              return;
            }
            applyScaleRef(scaleRefDraft);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              e.preventDefault();
              scaleRefCancelRef.current = true;
              onScaleRefChange?.(null);
              setScaleRefDraft('');
            }
          }}
        />
      )}
      {dimEdit && (() => {
        const wall = data.walls.find((entry) => entry.id === dimEdit.wallId);
        if (!wall) return null;
        const point = wallDimensionAnchor(wall, viewBox, pxPerIn);
        return (
          <input
            className="fp-dim-edit"
            value={dimEdit.value}
            autoFocus
            aria-label="Wall length"
            style={{
              left: `${((point.x - viewBox.x) / viewBox.w) * 100}%`,
              top: `${((point.y - viewBox.y) / viewBox.h) * 100}%`,
            }}
            onChange={(e) => setDimEdit({ ...dimEdit, value: e.target.value })}
            onBlur={() => commitDimEdit(dimEdit.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                e.preventDefault();
                setDimEdit(null);
              }
            }}
          />
        );
      })()}
      {tool === 'dimension' && measureEdit != null && measureAnchor && measureDriven && (() => {
        const a = resolveDimPoint(data, measureAnchor);
        const b = resolveDimPoint(data, measureDriven);
        if (!a || !b) return null;
        return (
          <input
            className="fp-dim-edit"
            value={measureEdit}
            autoFocus
            aria-label="Distance from reference"
            style={{
              left: `${((((a.x + b.x) / 2) - viewBox.x) / viewBox.w) * 100}%`,
              top: `${((((a.y + b.y) / 2) - viewBox.y) / viewBox.h) * 100}%`,
            }}
            onChange={(e) => setMeasureEdit(e.target.value)}
            onBlur={() => {
              if (measureCancelRef.current) {
                measureCancelRef.current = false;
                return;
              }
              commitMeasureEdit(measureEdit);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                e.preventDefault();
                measureCancelRef.current = true;
                setMeasureEdit(null);
                setMeasureDriven(null);
              }
            }}
          />
        );
      })()}
    </div>
  );
};

export default FloorPlanCanvas;
